import { Box, Text } from '@user-portal/wikr-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CANCELLATION_REASONS_MODAL_ID } from 'constants/analytics';

import { sendAnalyticTakeQuizClick } from 'services/Analytics';
import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';

import useModals from 'modals/hooks';

import { getValuesByKey } from 'helpers/utils';

import { CustomPrimaryButton } from 'components/Buttons';
import OutlinedButton from 'components/Buttons/OutlinedButton';
import Modal from 'components/Modal';

import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import HeaderWithLogo from '../../components/HeaderWithLogo';
import { useGetTexts } from '../../hooks/useGetTexts';
import useCancellationReasonSubmit from '../hooks/useCancellationReasonSubmit';
import { IReason } from '../types';

import { reasons } from './constants';

import * as S from './styled';

const CancellationReasonModalControl = () => {
    const { t } = useTranslation();
    const { onSubmitSurvey } = useCancellationReasonSubmit();
    const { onCancelSubscription, cancellationCandidates } = useCancelSubscriptionFlow();
    const { closeModal } = useModals();
    const subscription_id = useMemo(
        () => getValuesByKey(cancellationCandidates, 'external_id'),
        [cancellationCandidates]
    );

    const [selectedReason, setReason] = useState<IReason | null>(null);

    useEffect(() => {
        trackScreenLoad(CANCELLATION_REASONS_MODAL_ID, { subscription_id });
    }, [subscription_id]);

    const submitSurvey = () => {
        if (!selectedReason) return;

        onSubmitSurvey({
            reason: selectedReason,
        });
    };

    const handleSelectReason = (reason: IReason) => () => {
        setReason(reason);
        sendAnalyticTakeQuizClick();
    };

    const isMultiSubs = cancellationCandidates && cancellationCandidates.length > 1;

    const titleText = isMultiSubs
        ? 'subscription.cancellationReason.title.multi'
        : 'subscription.cancellationReason.title.single';

    const { title, subtitle } = useGetTexts({
        titleText,
        subtitleText: isMultiSubs
            ? 'subscription.cancellationReason.subtitle.multi'
            : 'subscription.cancellationReason.subtitle.single',
    });

    return (
        <Modal customStyles={{ padding: 0 }} isOpen fullscreen dataLocator="reasonModal" withRoundedCorners={false}>
            <HeaderWithLogo onBackButtonClick={closeModal} />
            <Box $paddingTop={24} $paddingBottom={16} className="container-sm">
                <Text type="h5" mb={12}>
                    {t(title)}
                </Text>
                <Text color="text-secondary" mb={24}>
                    {subtitle}
                </Text>

                <Box $mb={32}>
                    {reasons.map((reason) => {
                        const { title, value, shortKey } = reason;

                        return (
                            <S.ReasonItem
                                key={value}
                                onClick={handleSelectReason(reason)}
                                isSelected={value === selectedReason?.value}
                                data-locator={`cancelSubOption_${shortKey}`}
                            >
                                <Text type="medium-text" bold>
                                    {t(title)}
                                </Text>
                            </S.ReasonItem>
                        );
                    })}
                </Box>

                <CustomPrimaryButton
                    onClick={submitSurvey}
                    dataLocator="continue"
                    disabled={Boolean(!selectedReason)}
                    mb={16}
                >
                    {t('basics.continue')}
                </CustomPrimaryButton>

                <OutlinedButton
                    dataLocator="cancel"
                    onClick={onCancelSubscription}
                    backgroundColor="surface-main"
                    text={t('basics.button.cancelSubscription')}
                    mb={20}
                />
            </Box>
        </Modal>
    );
};

export default CancellationReasonModalControl;
